export function b64toBlob(b64Data: string, contentType: string): Blob | null {
    if (!b64Data) return null;
  
    // Remove the Base64 metadata if present
    b64Data = b64Data.replace(/^data:[^;]+;base64,/, '');
  
    // Convert Base64 to binary
    const byteArray = Uint8Array.from(atob(b64Data), char => char.charCodeAt(0));
  
    // Return the Blob with the correct content type
    return new Blob([byteArray], { type: contentType });
  }

/**
 * Decode a PDF (or any base64 data URL) to raw bytes.
 * Use for masked viewer output (e.g. getViewerDataUrl) so metadata matches what the user sees.
 */
export function dataUrlToPdfBytes(dataUrl: string): Uint8Array | null {
  if (!dataUrl?.startsWith('data:')) return null;
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  const b64 = dataUrl.slice(comma + 1).trim();
  try {
    const binary = atob(b64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

/** Decode a viewer PDF src (`data:` or `blob:`) to raw bytes. */
export async function pdfSrcToBytes(src: string): Promise<Uint8Array | null> {
  if (!src) return null;
  if (src.startsWith('data:')) {
    return dataUrlToPdfBytes(src);
  }
  if (src.startsWith('blob:')) {
    try {
      const res = await fetch(src);
      if (!res.ok) return null;
      return new Uint8Array(await res.arrayBuffer());
    } catch {
      return null;
    }
  }
  return null;
}

/** Viewer src as a data URL for postMessage (second screen). Same bytes ngx is showing. */
export async function pdfSrcToDataUrl(src: string): Promise<string | null> {
  if (!src) return null;
  if (src.startsWith('data:')) return src;
  const bytes = await pdfSrcToBytes(src);
  if (!bytes?.length) return null;
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return `data:application/pdf;base64,${btoa(binary)}`;
}
